import { Theme } from '@mui/material/styles'

// ----------------------------------------------------------------------

/**
 * One focus ring for every interactive element.
 *
 * Before this the app had no `focus-visible` styling anywhere: keyboard users saw
 * either a browser default or, where a component reset it, nothing at all.
 *
 * Drawn with `outline` + `outline-offset` rather than box-shadow. Box-shadow was
 * the first attempt and it loses: Button ships `disableElevation`, whose
 * `box-shadow: none` lands on the same class at the same specificity, so which
 * one won depended on emitted order and varied between button instances. Outline
 * is untouched by every shadow rule (elevation, the hover colorShadow), follows
 * border-radius in current browsers, and is the property that actually means
 * "focus". The offset supplies the gap the two-layer shadow used to draw.
 *
 * `:focus-visible` keeps it off mouse clicks.
 *
 * Exported so the components that own their own overrides (buttons, inputs,
 * links) use the same ring instead of re-deriving it.
 */
export function focusRing(theme: Theme) {
  const isLight = theme.palette.mode === 'light'
  const ringColor = isLight ? theme.palette.primary.main : theme.palette.primary.light

  return {
    outline: `2px solid ${ringColor}`,
    outlineOffset: 2,
  }
}

/**
 * Only MuiButtonBase is set here, on purpose: Button, IconButton, MenuItem, Tab,
 * Checkbox, Radio and ListItemButton all extend it, so one rule covers them
 * without colliding with the per-component overrides in this folder (they are
 * merged with a shallow Object.assign in ./index.ts, so a repeated component key
 * would silently drop the other file's styles).
 */
export default function Focus(theme: Theme) {
  const ring = focusRing(theme)

  return {
    MuiButtonBase: {
      defaultProps: {
        // The ripple is replaced by designed hover/active states.
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': ring,
          '&:focus-visible': ring,
        },
      },
    },
  }
}
